import { useState, useMemo, useCallback } from 'react';
import type {
  DateRangePreset,
  AnalyticsQueryParams,
} from '../types/analytics.types';

type AnalyticsFiltersState = {
  dateRange: DateRangePreset;
  startDate: string | null;
  endDate: string | null;
  partnerCompanyId: string | null;
  excludeInternal: boolean;
  queryParams: AnalyticsQueryParams;
  setDateRange: (preset: DateRangePreset) => void;
  setCustomRange: (start: string, end: string) => void;
  setPartnerCompanyId: (id: string | null) => void;
  setExcludeInternal: (value: boolean) => void;
  resetFilters: () => void;
};

const DEFAULT_DATE_RANGE: DateRangePreset = '30d';

export const useAnalyticsFilters = (): AnalyticsFiltersState => {
  const [dateRange, setDateRangeState] = useState<DateRangePreset>(DEFAULT_DATE_RANGE);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [partnerCompanyId, setPartnerCompanyId] = useState<string | null>(null);
  const [excludeInternal, setExcludeInternal] = useState(true);

  const setDateRange = useCallback((preset: DateRangePreset) => {
    setDateRangeState(preset);
    if (preset !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  }, []);

  const setCustomRange = useCallback((start: string, end: string) => {
    setDateRangeState('custom');
    setStartDate(start);
    setEndDate(end);
  }, []);

  const resetFilters = useCallback(() => {
    setDateRangeState(DEFAULT_DATE_RANGE);
    setStartDate(null);
    setEndDate(null);
    setPartnerCompanyId(null);
    setExcludeInternal(true);
  }, []);

  const queryParams = useMemo<AnalyticsQueryParams>(() => {
    const params: AnalyticsQueryParams = {
      dateRange,
      excludeInternal,
    };
    if (dateRange === 'custom' && startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (partnerCompanyId) {
      params.partnerCompanyId = partnerCompanyId;
    }
    return params;
  }, [dateRange, startDate, endDate, partnerCompanyId, excludeInternal]);

  return {
    dateRange,
    startDate,
    endDate,
    partnerCompanyId,
    excludeInternal,
    queryParams,
    setDateRange,
    setCustomRange,
    setPartnerCompanyId,
    setExcludeInternal,
    resetFilters,
  };
};
